import { toast } from 'sonner';
import { DialogDescription, DialogTitle } from '@radix-ui/react-dialog';
import { useDispatch } from 'react-redux';
import { Mail, Undo2 } from 'lucide-react';
import { z } from 'zod';
import { useForgetPasswordForm } from '../useAuthForm';
import { DialogFooter, DialogHeader } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { forgetPasswordSchema } from '../auth.schema';
import {
  setShowForgetPasswordDialog,
  setShowLoginDialog,
} from '@/redux/global/global.slice';
import Divider from '@/components/ui/divider';
import { useSendForgetPasswordEmailMutation } from '@/redux/auth/auth.api.slice';

function ForgetPasswordForm() {
  const dispatch = useDispatch();
  const forgetPasswordForm = useForgetPasswordForm();
  const [
    sendPasswordResetEmail,
    { isLoading: isSendPasswordResetEmailLoading },
  ] = useSendForgetPasswordEmailMutation();

  async function handleForgetPasswordSubmit(
    values: z.infer<typeof forgetPasswordSchema>
  ) {
    try {
      const data = await sendPasswordResetEmail(values).unwrap();
      if (data.status !== 'success') {
        toast.error('Login Failed', {
          description: (data as any)?.errorMessage || 'Something went wrong',
        });
      }
      dispatch(setShowForgetPasswordDialog(false));
      toast.success('Reset mail sent successfully', {
        description: 'Check your inbox for the password reset link',
      });
    } catch (error: any) {
      toast.error('Failed to send reset mail', {
        description: error?.data?.errorMessage || 'Something went wrong',
      });
    }
  }

  const handleOpenLoginModal = () => {
    dispatch(setShowForgetPasswordDialog(false));
    dispatch(setShowLoginDialog(true));
  };
  return (
    <Form {...forgetPasswordForm}>
      <form
        onSubmit={forgetPasswordForm.handleSubmit(handleForgetPasswordSubmit)}
      >
        <DialogHeader>
          <DialogTitle className="font-primary text-lg font-bold text-center">
            Forget Password
          </DialogTitle>
          <DialogDescription className="text-center font-secondary text-[16px] text-[#666666]">
            Already having an account? <br />
            Continue now!
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col py-2 gap-4">
          <div className="flex flex-col gap-2">
            <FormField
              control={forgetPasswordForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="Enter your email address"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <DialogFooter className="mt-2">
          <div className="flex flex-col gap-2 w-full">
            <Button
              type="submit"
              className="w-full bg-black font-bold"
              disabled={isSendPasswordResetEmailLoading}
            >
              <span className="flex items-center gap-2">
                <Mail size="18px" />
                <span>Send password reset to mail</span>
              </span>
            </Button>
          </div>
        </DialogFooter>
      </form>
      <Divider text="Or" />
      <div className="flex flex-col gap-2">
        <Button
          onClick={handleOpenLoginModal}
          className="w-full"
          variant="outline"
          type="button"
          disabled={isSendPasswordResetEmailLoading}
        >
          <span className="flex items-center gap-2">
            <Undo2 size="18px" />
            <span>Back to login</span>
          </span>
        </Button>
      </div>
    </Form>
  );
}

export default ForgetPasswordForm;
